import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { message } from 'antd';
import Button from '../../Button';
import Table from '../../_table/Table/Table';
import Anchor from '../../link/Anchor';
import ExcelUploadForm from '../../form/ExcelUploadForm';
import { AntdColumn, AntdColumnUtilType, AntdTableOption, Option } from '../../../types/common/table';
import useClientTableList from '../../../hooks/common/useClientTableList';
import { useTableOption } from '../../../hooks/common/useTableOption';
import { ExcelTableStyle } from './style';
import { changeAntdSortToTableSort, changeTableSortToAntdSort } from '../../../lib/utils/tableUtils';

interface ExcelTableProps<Item, Filter> {
  list: Item[];
  excelTemplateUrl: string;
  columns: (utils: AntdColumnUtilType<Item, Filter>) => AntdColumn<Item>[];
  initialOption?: Option<Filter>;
  rowClassName?: (record: Item) => string | null;
  canDrag: boolean;
  onAddItem: () => void;
  onExcelUpload: (file: File) => void;
  onDownloadExcel: (idList: number[]) => void;
  onDragEnd: (newList: { list: Item[]; idList: number[] }) => void;
  onDeleteList: (targetList: number[]) => void;
}

const ExcelTableInner = <Item extends { id: number }, Filter extends Record<string, unknown>>(
  props: ExcelTableProps<Item, Filter>,
  ref: Ref<{ resetOption: () => void }>,
): JSX.Element => {
  const {
    list,
    excelTemplateUrl,
    columns,
    initialOption,
    rowClassName,
    canDrag,
    onAddItem,
    onExcelUpload,
    onDownloadExcel,
    onDragEnd,
    onDeleteList,
  } = props;

  const [prevList, setPrevList] = useState<Item[]>([]);
  const [selectedIdList, setSelectedIdList] = useState<number[]>([]);
  const { sortOption, filterOption, pagination, changeOption, resetOption } = useTableOption(initialOption);
  const { list: visibleList, totalCnt } = useClientTableList({
    list,
    option: { sortOption, filterOption, pagination },
    changeOption,
  });

  //컴포넌트 외부에서 리스트 제거시 선택한 리스트 리셋
  if (list.length !== prevList.length) {
    setPrevList(list);
    setSelectedIdList([]);
  }

  const handleExcelDownload = (): void => {
    if (list.length === 0) {
      message.error('엑셀을 다운로드할 데이터가 없습니다.');
      return;
    }
    onDownloadExcel(list.map((item) => item.id));
  };

  const handleDeleteList = (): void => {
    onDeleteList(selectedIdList);
    setSelectedIdList([]);
  };

  useImperativeHandle(ref, () => ({
    resetOption,
  }));

  return (
    <ExcelTableStyle>
      <div className="item-counter">
        등록된 아이템 개수 : <span>{list.length}</span> ㅣ 선택된 아이템 개수 : <span>{selectedIdList.length}</span>
      </div>
      <div className="button-wrap">
        <div className="right">
          <Button isRadius color="black" text="아이템 추가" onClick={onAddItem} type="button" />
          <Button isRadius color="light" text="필터 초기화" onClick={resetOption} type="button" />
        </div>
        <div className="left">
          <Anchor text="업로드 템플릿" isUnderLine color="primary" href={excelTemplateUrl} target="_blank" />

          <ExcelUploadForm onChange={onExcelUpload} btnText="엑셀 업로드" />

          <Button isRadius color="light" text="엑셀 다운로드" onClick={handleExcelDownload} type="button" />
        </div>
      </div>
      <Table
        rowKey={(record: Item): number => record.id}
        columns={columns}
        list={visibleList}
        size="middle"
        scroll={{ x: true }}
        bordered={true}
        onCheck={setSelectedIdList}
        selectedList={selectedIdList}
        getList={(option: AntdTableOption<Filter>): void =>
          changeOption({
            ...option,
            sortOption: option.sortOption && changeAntdSortToTableSort(option.sortOption),
          })
        }
        pagination={{ ...pagination, totalCnt }}
        filterOption={filterOption}
        sortOption={changeTableSortToAntdSort(sortOption)}
        rowClassName={rowClassName}
        canDrag={canDrag}
        onDragEnd={onDragEnd}
      />
      <div className="button-wrap">
        <Button
          isRadius
          color="primary"
          text="삭제"
          disabled={!selectedIdList.length}
          onClick={handleDeleteList}
          type="button"
        />
      </div>
    </ExcelTableStyle>
  );
};

const ExcelTable = forwardRef(ExcelTableInner) as <Item extends { id: number }, Filter extends Record<string, unknown>>(
  props: ExcelTableProps<Item, Filter> & { ref: Ref<{ resetOption: () => void }> },
) => JSX.Element;

export default ExcelTable;
